import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "react-query";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import * as api from "apis/kpiTimeline";

export function useKpiTimelines(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["kpiTimelines", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getKpiTimelines(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export function useKpiTimelineUsers(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["kpiTimeline/users", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getKpiTimelineUsers(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export function useKpiTimelineForms(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["kpiTimeline/forms", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getKpiTimelineForms(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export const useKpiTimelineFormsSuggestion = (payload = {}, options = {}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useInfiniteQuery(
    ["kpiTimeline/forms/suggestion", payload],
    async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getKpiTimelineForms({ page: pageParam, ...payload });
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
};

export function useKpiTimeline(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["kpiTimeline", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getKpiTimeline(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export const useAddKpiTimelinePeriod = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.addKpiTimelinePeriod(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useAddKpiTimelineUsers = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.addKpiTimelineUsers(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useAddKpiTimelineEvaluators = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.addKpiTimelineEvaluators(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEditKpiTimelinePeriod = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.editKpiTimelinePeriod(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEditKpiTimelineUsers = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.editKpiTimelineUsers(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEditKpiTimelineEvaluators = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.editKpiTimelineEvaluators(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useDeleteKpiTimeline = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  return useMutation((payload) => api.deleteKpiTimeline(payload?.id), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["kpiTimelines", payload?.params]);
      const previousData = queryClient.getQueryData([
        "kpiTimelines",
        payload?.params,
      ]);
      queryClient.setQueryData(["kpiTimelines", payload?.params], (old) => {
        const newData = old?.data?.filter((item) => item.id !== payload?.id);
        return { ...old, data: newData };
      });
      return { previousData };
    },
    onSuccess: ({ data }, payload) => {
      if (data?.success) {
        queryClient.invalidateQueries(["kpiTimelines", payload?.params]);
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(
        ["kpiTimelines", payload?.params],
        context.previousData
      );
      message.error(t("error.failed_save_data_to_server"));
    },
  });
};

export const useActivateKpiTimeline = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  return useMutation((payload) => api.activateKpiTimeline(payload?.id), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["kpiTimelines", payload?.params]);
      const previousData = queryClient.getQueryData([
        "kpiTimelines",
        payload?.params,
      ]);
      queryClient.setQueryData(["kpiTimelines", payload?.params], (old) => {
        const newData = old?.data?.map((item) => {
          if (item.id === payload?.id) {
            return { ...item, is_active: 1 };
          }
          return item;
        });
        return { ...old, data: newData };
      });
      return { previousData };
    },
    onSuccess: ({ data }, payload) => {
      if (data?.success) {
        queryClient.invalidateQueries(["kpiTimelines", payload?.params]);
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(
        ["kpiTimelines", payload?.params],
        context.previousData
      );
      message.error(t("error.failed_save_data_to_server"));
    },
  });
};

export const useDeactivateKpiTimeline = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  return useMutation((payload) => api.deactivateKpiTimeline(payload?.id), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["kpiTimelines", payload?.params]);
      const previousData = queryClient.getQueryData([
        "kpiTimelines",
        payload?.params,
      ]);
      queryClient.setQueryData(["kpiTimelines", payload?.params], (old) => {
        const newData = old?.data?.map((item) => {
          if (item.id === payload?.id) {
            return { ...item, is_active: 0 };
          }
          return item;
        });
        return { ...old, data: newData };
      });
      return { previousData };
    },
    onSuccess: ({ data }, payload) => {
      if (data?.success) {
        queryClient.invalidateQueries(["kpiTimelines", payload?.params]);
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(
        ["kpiTimelines", payload?.params],
        context.previousData
      );
      message.error(t("error.failed_save_data_to_server"));
    },
  });
};

export const useArchiveKpiTimeline = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  return useMutation((payload) => api.archiveKpiTimeline(payload?.id), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["kpiTimelines", payload?.params]);
      const previousData = queryClient.getQueryData([
        "kpiTimelines",
        payload?.params,
      ]);
      queryClient.setQueryData(["kpiTimelines", payload?.params], (old) => {
        const newData = old?.data?.filter((item) => item.id !== payload?.id);
        return { ...old, data: newData };
      });
      return { previousData };
    },
    onSuccess: ({ data }, payload) => {
      if (data?.success) {
        queryClient.invalidateQueries(["kpiTimelines", payload?.params]);
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(
        ["kpiTimelines", payload?.params],
        context.previousData
      );
      message.error(t("error.failed_save_data_to_server"));
    },
  });
};

export const useUnarchiveKpiTimeline = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  return useMutation((payload) => api.unarchiveKpiTimeline(payload?.id), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["kpiTimelines", payload?.params]);
      const previousData = queryClient.getQueryData([
        "kpiTimelines",
        payload?.params,
      ]);
      queryClient.setQueryData(["kpiTimelines", payload?.params], (old) => {
        const newData = old?.data?.filter((item) => item.id !== payload?.id);
        return { ...old, data: newData };
      });
      return { previousData };
    },
    onSuccess: ({ data }, payload) => {
      if (data?.success) {
        queryClient.invalidateQueries(["kpiTimelines", payload?.params]);
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(
        ["kpiTimelines", payload?.params],
        context.previousData
      );
      message.error(t("error.failed_save_data_to_server"));
    },
  });
};
